/**
 * Página de Perfil do Usuário do Sistema MEDICARE
 * Permite visualizar e editar informações do perfil
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  VStack,
  Text,
  Avatar,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Divider,
  IconButton,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormErrorMessage,
  InputGroup,
  InputRightElement,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Textarea,
} from '@chakra-ui/react';
import {
  FiEdit2,
  FiSave,
  FiX,
  FiEye,
  FiEyeOff,
  FiCamera,
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
} from 'react-icons/fi';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/ToastContext';
import { UsuariosService } from '../../services/usuarios';
import { UpdateProfileResponse } from '../../types';
import { formatPhoneDisplay, formatPhoneInput, cleanPhone, isValidPhone } from '../../utils/phoneFormatter';
import { getAvatarUrl } from '../../utils/avatarUtils';

interface ProfileFormData {
  nome: string;
  telefone: string;
  endereco: string;
  bio: string;
}

interface PasswordFormData {
  senhaAtual: string;
  novaSenha: string;
  confirmarSenha: string;
}

const USER_TYPES = {
  admin: { label: 'Administrador', color: 'purple' },
  medico: { label: 'Médico', color: 'blue' },
  enfermeiro: { label: 'Enfermeiro', color: 'green' },
  paciente: { label: 'Paciente', color: 'gray' },
};

export const ProfilePage: React.FC = () => {
  const { user, dispatch } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const queryClient = useQueryClient();
  
  // Query para buscar dados do perfil
  const { data: profileData, isLoading: isLoadingProfile, refetch: refetchProfile } = useQuery(
    'userProfile',
    UsuariosService.obterPerfil,
    {
      onSuccess: (response: any) => {
        // O backend retorna { sucesso: true, dados: { usuario: {...} } }
        // Mas o React Query encapsula em { success: true, data: {...} }
        const apiData = response.data || response;
        if (apiData.sucesso && apiData.dados?.usuario) {
          // Atualiza o contexto com os dados mais recentes
          dispatch({
            type: 'ATUALIZAR_USUARIO',
            payload: apiData.dados.usuario,
          });
        }
      },
      onError: (error) => {
        console.error('Erro ao carregar perfil:', error);
        showError('Erro ao carregar dados do perfil');
      },
    }
  );

  // Usa os dados do contexto (que já foram atualizados pelo onSuccess)
  const currentUser = user;
  
  // Corrigir o acesso aos dados da API - usar profileData.data.dados.usuario
  const apiUserData = profileData?.data?.dados?.usuario;
  
  const [isEditing, setIsEditing] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { isOpen: isPasswordModalOpen, onOpen: onPasswordModalOpen, onClose: onPasswordModalClose } = useDisclosure();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const readOnlyBg = useColorModeValue('gray.50', 'gray.700');

  // Form para dados do perfil
  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors, isSubmitting: isSubmittingProfile },
    reset: resetProfile,
    setValue,
  } = useForm<ProfileFormData>({
    defaultValues: {
      nome: currentUser?.nome || '',
      telefone: currentUser?.telefone || '',
      endereco: currentUser?.endereco || '',
      bio: currentUser?.bio || '',
    },
  });

  // Form para alteração de senha
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors, isSubmitting: isSubmittingPassword },
    reset: resetPassword,
    watch,
  } = useForm<PasswordFormData>();

  const novaSenha = watch('novaSenha');

  // Atualiza o formulário quando os dados do perfil chegam
  useEffect(() => {
    if (currentUser) {
      const formattedPhone = currentUser.telefone ? formatPhoneInput(currentUser.telefone) : '';
      setPhoneValue(formattedPhone);
      
      resetProfile({
        nome: currentUser.nome || '',
        telefone: currentUser.telefone || '',
        endereco: currentUser.endereco || '',
        bio: currentUser.bio || '',
      });
    }
  }, [currentUser]); // Removida a dependência resetProfile

  // Mutation para atualizar perfil
  const updateProfileMutation = useMutation(UsuariosService.atualizarPerfil, {
    onSuccess: (response: any) => {
      // O backend retorna { success: true, data: { usuario: {...} } }
      // Mas o ApiService encapsula em { success: true, data: {...} }
      const apiData = response.data || response;
      
      if (response.success && apiData.data?.usuario) {
        // Atualiza o contexto com os dados mais recentes
        dispatch({
          type: 'ATUALIZAR_USUARIO',
          payload: apiData.data.usuario,
        });
        
        showSuccess('Perfil atualizado com sucesso!');
        setIsEditing(false);
        refetchProfile(); // Recarrega os dados do perfil
        
        // Invalidar cache do React Query para forçar re-render
        queryClient.invalidateQueries('userProfile');
      } else {
        console.error('ProfilePage: Estrutura de resposta inesperada:', response);
        showError('Erro na estrutura de resposta da API');
      }
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar perfil:', error);
      showError(error.response?.data?.message || error.response?.data?.mensagem || 'Erro ao atualizar perfil');
    },
  });

  // Mutation para alterar senha
  const changePasswordMutation = useMutation(
    (data: PasswordFormData) => UsuariosService.changePassword(data),
    {
      onSuccess: () => {
        showSuccess('Senha alterada com sucesso!');
        resetPassword();
        onPasswordModalClose();
      },
      onError: () => {
        showError('Erro ao alterar senha');
      },
    }
  );

  // Mutation para upload de avatar
  const uploadAvatarMutation = useMutation(
    (file: File) => UsuariosService.uploadAvatar(file),
    {
      onSuccess: (response: any) => {
        console.log('Avatar upload response:', response);
        console.log('Avatar upload response.data:', response.data);
        console.log('Avatar upload response.data.dados:', response.data?.dados);
        console.log('Avatar upload response.data.dados.avatar:', response.data?.dados?.avatar);
        showSuccess('Avatar atualizado com sucesso!');
        setIsUploadingAvatar(false);
        
        // Atualizar o contexto com o novo avatar
        // A resposta do backend tem a estrutura: { sucesso: true, dados: { avatar: "..." } }
        const novoAvatar = response.data?.dados?.avatar;
        if (novoAvatar && currentUser) {
          console.log('Atualizando contexto com novo avatar:', novoAvatar);
          
          // Atualizar tanto a propriedade direta quanto dados_adicionais
          const usuarioAtualizado = {
            ...currentUser,
            avatar: novoAvatar,
            dados_adicionais: {
              ...currentUser.dados_adicionais,
              avatar: novoAvatar
            }
          };
          
          console.log('Usuário atualizado:', usuarioAtualizado);
          console.log('Avatar URL que será usado:', getAvatarUrl(novoAvatar));
          
          dispatch({
            type: 'ATUALIZAR_USUARIO',
            payload: usuarioAtualizado,
          });
        } else {
          console.log('Não foi possível atualizar o avatar no contexto:', { novoAvatar, currentUser });
        }
        // Forçar re-render do componente
        setIsUploadingAvatar(false);
        
        // Invalidar cache do React Query para forçar re-render
        queryClient.invalidateQueries('userProfile');
        
        // Forçar atualização do estado local
        setTimeout(() => {
          refetchProfile();
        }, 100);
      },
      onError: (error: any) => {
        console.error('Erro ao fazer upload do avatar:', error);
        showError(error.response?.data?.mensagem || 'Erro ao fazer upload do avatar');
        setIsUploadingAvatar(false);
      },
    }
  );

  const onSubmitProfile = (data: ProfileFormData) => {
    // Limpar formatação do telefone antes de enviar
    const cleanedData = {
      ...data,
      telefone: data.telefone ? cleanPhone(data.telefone) : '',
    };
    
    updateProfileMutation.mutate(cleanedData);
  };

  const onSubmitPassword = (data: PasswordFormData) => {
    changePasswordMutation.mutate(data);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Restaurar valor formatado do telefone
    const formattedPhone = currentUser?.telefone ? formatPhoneInput(currentUser.telefone) : '';
    setPhoneValue(formattedPhone);
    resetProfile();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  // Função para lidar com o upload de avatar
  const handleAvatarClick = () => {
    if (isEditing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showError('Tipo de arquivo não permitido. Apenas imagens são aceitas.');
        return;
      }

      // Validar tamanho (5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('Arquivo muito grande. Tamanho máximo: 5MB.');
        return;
      }

      setIsUploadingAvatar(true);
      uploadAvatarMutation.mutate(file);
    }
  };

  if (!currentUser && !isLoadingProfile) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text>Carregando perfil...</Text>
      </Container>
    );
  }

  if (isLoadingProfile) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text>Carregando dados do perfil...</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Cabeçalho */}
        <Flex justify="space-between" align="center">
          <Heading size="lg">Meu Perfil</Heading>
          {!isEditing && (
            <Button
              leftIcon={<FiEdit2 />}
              colorScheme="medicare"
              onClick={() => setIsEditing(true)}
            >
              Editar Perfil
            </Button>
          )}
        </Flex>

        <Grid templateColumns={{ base: '1fr', lg: '1fr 2fr' }} gap={6}>
          {/* Card de informações básicas */}
          <GridItem>
            <Card>
              <CardBody>
                <VStack spacing={6}>
                  {/* Avatar e informações principais */}
                  <VStack spacing={4}>
                    <Box position="relative">
                      <Avatar
                        key={currentUser?.avatar || 'no-avatar'} // Força re-render quando avatar muda
                        size="2xl"
                        name={currentUser?.nome}
                        src={getAvatarUrl(currentUser?.avatar)}
                      />
                      {isEditing && (
                        <IconButton
                          aria-label="Alterar foto"
                          icon={<FiCamera />}
                          size="sm"
                          colorScheme="medicare"
                          borderRadius="full"
                          position="absolute"
                          bottom="0"
                          right="0"
                          onClick={handleAvatarClick}
                          isLoading={isUploadingAvatar}
                        />
                      )}
                      
                      {/* Input de arquivo oculto */}
                      <Input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        display="none"
                      />
                    </Box>
                    
                    <VStack spacing={2} textAlign="center">
                      <Heading size="md">{currentUser?.nome}</Heading>
                      <Badge
                        colorScheme={USER_TYPES[currentUser?.tipo_usuario as keyof typeof USER_TYPES]?.color || 'gray'}
                        variant="subtle"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        {USER_TYPES[currentUser?.tipo_usuario as keyof typeof USER_TYPES]?.label || currentUser?.tipo_usuario}
                      </Badge>
                    </VStack>
                  </VStack>

                  <Divider />

                  {/* Estatísticas do usuário */}
                  <VStack spacing={4} w="full">
                    <Stat textAlign="center">
                      <StatLabel>Membro desde</StatLabel>
                      <StatNumber fontSize="md">
                        {currentUser?.data_criacao ? formatDate(currentUser.data_criacao) : 'N/A'}
                      </StatNumber>
                    </Stat>
                    
                    <Stat textAlign="center">
                      <StatLabel>Último acesso</StatLabel>
                      <StatNumber fontSize="md">
                        {currentUser?.ultimo_acesso ? formatDate(currentUser.ultimo_acesso) : 'Nunca'}
                      </StatNumber>
                    </Stat>
                  </VStack>

                  <Divider />

                  {/* Ações */}
                  <VStack spacing={3} w="full">
                    <Button
                      w="full"
                      variant="outline"
                      onClick={onPasswordModalOpen}
                    >
                      Alterar Senha
                    </Button>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          </GridItem>

          {/* Card de dados detalhados */}
          <GridItem>
            <Card>
              <CardHeader>
                <Flex justify="space-between" align="center">
                  <Heading size="md">Informações Pessoais</Heading>
                  {isEditing && (
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<FiX />}
                        onClick={handleCancelEdit}
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="medicare"
                        leftIcon={<FiSave />}
                        onClick={handleSubmitProfile(onSubmitProfile)}
                        isLoading={isSubmittingProfile}
                      >
                        Salvar
                      </Button>
                    </HStack>
                  )}
                </Flex>
              </CardHeader>
              <CardBody>
                <form onSubmit={handleSubmitProfile(onSubmitProfile)}>
                  <VStack spacing={6}>
                    <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} w="full">
                      {/* Nome */}
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <FormControl isInvalid={!!profileErrors.nome}>
                          <FormLabel>Nome completo</FormLabel>
                          {isEditing ? (
                            <Input
                              {...registerProfile('nome', {
                                required: 'Nome é obrigatório',
                              })}
                            />
                          ) : (
                            <Text py={2} px={3} bg={readOnlyBg} borderRadius="md">
                              {currentUser?.nome}
                            </Text>
                          )}
                          <FormErrorMessage>
                            {profileErrors.nome?.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      {/* Email */}
                      <GridItem>
                        <FormControl>
                          <FormLabel>Email</FormLabel>
                          <Text py={2} px={3} bg={readOnlyBg} borderRadius="md">
                              {currentUser?.email}
                            </Text>
                          <Text fontSize="sm" color="gray.500" mt={1}>
                            O email não pode ser alterado
                          </Text>
                        </FormControl>
                      </GridItem>

                      {/* Telefone */}
                      <GridItem>
                        <FormControl isInvalid={!!profileErrors.telefone}>
                          <FormLabel>Telefone</FormLabel>
                          {isEditing ? (
                            <Input
                              value={phoneValue}
                              onChange={(e) => {
                                const formatted = formatPhoneInput(e.target.value);
                                setPhoneValue(formatted);
                                // Atualizar o valor no react-hook-form
                                setValue('telefone', formatted);
                              }}
                              placeholder="(11) 99999-9999"
                              maxLength={15}
                            />
                          ) : (
                            <Text py={2} px={3} bg={readOnlyBg} borderRadius="md">
                              {formatPhoneDisplay(currentUser?.telefone)}
                            </Text>
                          )}
                          <FormErrorMessage>
                            {profileErrors.telefone?.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      {/* Endereço */}
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <FormControl isInvalid={!!profileErrors.endereco}>
                          <FormLabel>Endereço</FormLabel>
                          {isEditing ? (
                            <Input
                              {...registerProfile('endereco')}
                              placeholder="Rua, número, bairro, cidade - UF"
                            />
                          ) : (
                            <Text py={2} px={3} bg={readOnlyBg} borderRadius="md">
                              {currentUser?.endereco || 'Não informado'}
                            </Text>
                          )}
                          <FormErrorMessage>
                            {profileErrors.endereco?.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>

                      {/* Bio */}
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <FormControl isInvalid={!!profileErrors.bio}>
                          <FormLabel>Biografia</FormLabel>
                          {isEditing ? (
                            <Textarea
                              {...registerProfile('bio')}
                              placeholder="Conte um pouco sobre você..."
                              rows={4}
                            />
                          ) : (
                            <Text py={2} px={3} bg={readOnlyBg} borderRadius="md" minH="100px">
                               {currentUser?.bio || 'Nenhuma biografia adicionada'}
                             </Text>
                          )}
                          <FormErrorMessage>
                            {profileErrors.bio?.message}
                          </FormErrorMessage>
                        </FormControl>
                      </GridItem>
                    </Grid>
                  </VStack>
                </form>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>
      </VStack>

      {/* Modal para alterar senha */}
      <Modal isOpen={isPasswordModalOpen} onClose={onPasswordModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Alterar Senha</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmitPassword(onSubmitPassword)}>
            <ModalBody>
              <VStack spacing={4}>
                {/* Senha atual */}
                <FormControl isInvalid={!!passwordErrors.senhaAtual}>
                  <FormLabel>Senha atual</FormLabel>
                  <InputGroup>
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      {...registerPassword('senhaAtual', {
                        required: 'Senha atual é obrigatória',
                      })}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showCurrentPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        icon={showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>
                    {passwordErrors.senhaAtual?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* Nova senha */}
                <FormControl isInvalid={!!passwordErrors.novaSenha}>
                  <FormLabel>Nova senha</FormLabel>
                  <InputGroup>
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      {...registerPassword('novaSenha', {
                        required: 'Nova senha é obrigatória',
                        minLength: {
                          value: 6,
                          message: 'Senha deve ter pelo menos 6 caracteres',
                        },
                      })}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showNewPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        icon={showNewPassword ? <FiEyeOff /> : <FiEye />}
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>
                    {passwordErrors.novaSenha?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* Confirmar senha */}
                <FormControl isInvalid={!!passwordErrors.confirmarSenha}>
                  <FormLabel>Confirmar nova senha</FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...registerPassword('confirmarSenha', {
                        required: 'Confirmação de senha é obrigatória',
                        validate: (value) =>
                          value === novaSenha || 'Senhas não coincidem',
                      })}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                        icon={showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>
                    {passwordErrors.confirmarSenha?.message}
                  </FormErrorMessage>
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onPasswordModalClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                colorScheme="medicare"
                isLoading={isSubmittingPassword}
              >
                Alterar Senha
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default ProfilePage;